<template>
  <form class="invite-form" @submit.prevent="submit">
    <div class="invite-form__id">
      <FormInput
        v-model="userTemp.firstName"
        name="firstName"
        :error="firstNameError"
        :label="$t('page.admin-user-profile.first-name')"
        v-validate="'required'"
        has-gray-background
        class="input-first-name"
      />
      <FormInput
        v-model="userTemp.lastName"
        name="lastName"
        :error="lastNameError"
        :label="$t('page.admin-user-profile.last-name')"
        v-validate="'required'"
        has-gray-background
      />
    </div>
    <div class="invite__locale">
      <div class="preferences__label">
        <FormLabel>{{ $t('page.admin-user-profile.language') }}</FormLabel>
      </div>
      <div class="preferences__dropdown">
        <LocaleProvider ref="localeProvider">
          <template slot-scope="{ currentLocale, locales }">
            <BaseSelect
              name="locale"
              :value="currentLocale"
              :v-model="userTemp.locale"
              :items="locales"
              item-text="name"
              item-value="locale"
              @change="onLocaleChange"
            />
          </template>
        </LocaleProvider>
      </div>
    </div>
    <div class="invite-form__time">
      <div class="preferences__label">
        <FormLabel>{{ $t('page.admin-user-profile.time-zone') }}</FormLabel>
      </div>
      <div class="preferences__dropdown">
        <BaseSelect
          name="timezone"
          :v-model="userTemp.timezone"
          :value="userTemp.timezone"
          :items="timezoneItems"
          item-text="name"
          item-value="id"
          @change="onTimezoneChange"
          menuAuto
        />
      </div>
    </div>
    <div class="invite-form__passwords">
      <FormInput
        v-model="userTemp.clearPassword"
        name="password"
        ref="password"
        :type="passwordType"
        :error="passwordError"
        :label="$t('login.password')"
        v-validate="'required'"
        has-gray-background
        autocomplete="on"
      >
        <template #top-right>
          <button
            class="reset-password-form__toggle-password"
            type="button"
            @click.prevent="togglePassword"
            tabindex="-1"
          >
            <BaseIcon name="eye" v-if="passwordHidden" />
            <BaseIcon name="eye-off" v-else />
            {{ passwordHidden ? $t('reset-password.see') : $t('reset-password.hide') }}
          </button>
        </template>
      </FormInput>
      <FormInput
        v-model="confirmation"
        name="confirmation"
        ref="confirmation"
        :type="confirmationType"
        :error="confirmationError"
        :label="$t('reset-password.confirmation-input')"
        v-validate="'required|confirmed:password'"
        has-gray-background
        autocomplete="on"
      >
        <template #top-right>
          <button
            class="reset-password-form__toggle-password"
            type="button"
            @click.prevent="toggleConfirmation"
            tabindex="-1"
          >
            <BaseIcon name="eye" v-if="confirmationHidden" />
            <BaseIcon name="eye-off" v-else />
            {{ confirmationHidden ? $t('reset-password.see') : $t('reset-password.hide') }}
          </button>
        </template>
      </FormInput>
      <PasswordCriteria :password="userTemp.clearPassword" />
    </div>

    <FormCheckbox
      name="termsAndConditions"
      class="invite-form__terms"
      v-model="terms"
      :value="terms"
      v-validate="'required:true'"
      :error="termsAndConditionsError"
      terms
    >
      <template v-slot:label>
        <div class="invite-form__terms-label">
          <span class="invite-form__terms">
            {{ $t('invite.agree-start') }}
            <a :href="termsLink" target="blank" rel="noreferrer noopener" class="invite-form__terms-link">{{
              $t('invite.terms')
            }}</a>
            {{ $t('invite.agree-end') }}
          </span>
        </div>
      </template>
    </FormCheckbox>
    <ErrorNotice v-if="error" :error="error" />

    <div class="invite-form__actions">
      <FormButton
        type="submit"
        class="invite-form__actions-button"
        :is-outlined="!isFormValid"
        :isLoading="pending"
        :disabled="!isFormValid"
        >{{ $t('invite.button') }}</FormButton
      >
    </div>
  </form>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import ErrorNotice from '@/components/notice/ErrorNotice';
import FormButton from '@/components/form/FormButton.vue';
import FormCheckbox from '@/components/form/FormCheckbox.vue';
import FormInput from '@/components/form/FormInput.vue';
import FormLabel from '@/components/form/FormLabel.vue';
import LocaleProvider from '@/components/locale/LocaleProvider';
import BaseSelect from '@/components/base/BaseSelect';
import PasswordCriteria from '@/components/PasswordCriteria';

import { Config } from '@/config';
import { getBrowserTimezone } from '@/util/timezones/get-browser-timezone';
import { LOCALE } from '@/util/i18n/locale';
import { localize } from '@/util/i18n/localize';
import { timezones } from '@/util/timezones/timezones';
import { validatePassword } from '@/util/password-util';
import { mapGetters } from 'vuex';

export default {
  name: 'InviteForm',
  components: {
    BaseIcon,
    ErrorNotice,
    FormButton,
    FormCheckbox,
    FormInput,
    FormLabel,
    LocaleProvider,
    BaseSelect,
    PasswordCriteria,
  },
  data() {
    return {
      userTemp: {
        firstName: '',
        lastName: '',
        locale: '',
        timezone: '',
        clearPassword: '',
      },
      confirmation: '',
      passwordHidden: true,
      confirmationHidden: true,
      terms: false,
    };
  },
  inject: ['$validator'],
  computed: {
    ...mapGetters('acceptInvite', ['pending', 'error']),
    termsLink() {
      return this.currentLocale === LOCALE.EN_CA ? Config.terms.en : Config.terms.fr;
    },
    timezoneItems() {
      return timezones;
    },
    currentLocale() {
      return this.$i18n.locale;
    },
    currentTimezone() {
      return getBrowserTimezone();
    },
    firstNameError() {
      const error = this.errors.items.find((x) => x.field === 'firstName');
      return error ? error.msg : undefined;
    },
    lastNameError() {
      const error = this.errors.items.find((x) => x.field === 'lastName');
      return error ? error.msg : undefined;
    },
    passwordError() {
      const formError = this.errors.items.find((x) => x.field === 'password');
      return formError ? formError.msg : undefined;
    },
    confirmationError() {
      const formError = this.errors.items.find((x) => x.field === 'confirmation');
      return formError ? formError.msg : undefined;
    },
    termsAndConditionsError() {
      const error = this.errors.items.find((x) => x.field === 'termsAndConditions');
      return error ? error.msg : undefined;
    },
    passwordType() {
      return this.passwordHidden ? 'password' : 'text';
    },
    confirmationType() {
      return this.confirmationHidden ? 'password' : 'text';
    },
    passwordCriteria() {
      return validatePassword(this.userTemp.clearPassword);
    },
    isFormValid() {
      return (
        this.userTemp.firstName &&
        this.userTemp.lastName &&
        this.userTemp.locale &&
        this.userTemp.timezone &&
        this.userTemp.clearPassword &&
        this.passwordCriteria.valid &&
        this.userTemp.clearPassword === this.confirmation &&
        this.terms
      );
    },
  },
  methods: {
    onLocaleChange(locale) {
      this.userTemp.locale = locale;
      localize(locale);
    },
    onTimezoneChange(timezone) {
      this.userTemp.timezone = timezone;
    },
    togglePassword() {
      this.passwordHidden = !this.passwordHidden;
    },
    toggleConfirmation() {
      this.confirmationHidden = !this.confirmationHidden;
    },
    submit() {
      if (this.$validator.validate() && this.isFormValid) {
        this.$emit('submit', this.userTemp);
        this.$track.inviteSubmit();
      }
    },
  },
  mounted() {
    // Initializing these values ​​the first time
    this.userTemp.locale = this.currentLocale;
    this.userTemp.timezone = this.currentTimezone;
  },
  watch: {
    currentLocale() {
      this.userTemp.locale = this.currentLocale;
    },
  },
};
</script>

<style lang="scss" scoped>
.invite-form {
  &__id {
    justify-content: space-between;
    display: flex;
  }

  &__locale,
  &__time {
    display: block;
    align-self: center;
    justify-content: space-between;
    padding-top: 8px;
  }

  &__label {
    align-self: center;
  }

  &__passwords {
    padding-top: 8px;
    padding-bottom: 24px;
  }

  &__actions {
    justify-content: flex-end;
    display: flex;
    vertical-align: center;
    padding-top: 32px;
  }

  &__terms {
    padding-top: 12px;
    font-family: 'Raleway', sans-serif;
  }

  &__terms-label {
    padding-left: 8px;
  }

  &__terms-link {
    color: $color-accent;
    text-decoration: none;
    border-bottom: 1px solid $color-green;
  }
}

@media screen and (max-width: 1080px) {
  .invite-form {
    &__actions {
      justify-content: center;
      display: flex;
      vertical-align: center;
      margin-top: 32px;
    }
  }
}

@media screen and (max-width: 468px) {
  .invite-form {
    &__id {
      display: block;
    }
  }
}
</style>
